if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));

        // CUSTOM: Sort images by color on initial page load
        this.initializeColorSorting();
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initializeColorSorting() {
        // Wait a bit for the page to fully render
        setTimeout(() => {
          // Get the currently selected variant
          const variantSelects = this.querySelector('variant-selects');
          if (!variantSelects) return;

          const selectedVariantElement = variantSelects.querySelector('[data-selected-variant]');
          if (!selectedVariantElement) return;

          try {
            const variant = JSON.parse(selectedVariantElement.innerHTML);
            if (!variant) return;

            const mediaGallerySource = this.querySelector('media-gallery ul');
            if (!mediaGallerySource) return;

            const variantFeaturedMediaId = variant.featured_media?.id;

            if (variantFeaturedMediaId) {
              // Pass true for isInitialLoad to preserve the show more/less state
              this.sortImagesByColor(mediaGallerySource, variant, variantFeaturedMediaId, true);
            }
          } catch (error) {
            console.error('[Color Sort] Error during initialization:', error);
          }
        }, 300); // Small delay to ensure DOM is ready
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id, variant);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));
          /*CUSTOM*/
          /*updateSourceFromDestination(`ButtonPriceWrapper`, () => false);*/


          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId, variant) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });

        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // CUSTOM: Sort images by color based on selected variant
        // This must happen AFTER setActiveMedia to avoid being undone by prepend
        this.sortImagesByColor(mediaGallerySource, variant, variantFeaturedMediaId);

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      sortImagesByColor(mediaGallerySource, variant, variantFeaturedMediaId, isInitialLoad = false) {
        if (!variant || !mediaGallerySource) return;

        // Find the color option value from the variant
        let selectedColor = null;

        // Try to find color in option1, option2, or option3
        const variantSelects = this.querySelector('variant-selects');
        if (!variantSelects) return;

        const options = variantSelects.querySelectorAll('fieldset');

        options.forEach((fieldset, index) => {
          const legend = fieldset.querySelector('legend');
          if (legend) {
            // Get the text and remove ALL whitespace (including line breaks)
            const rawText = legend.textContent || legend.innerText || '';
            const optionName = rawText.replace(/\s+/g, ' ').trim().toLowerCase();

            // Check if this is the color option
            if (optionName.includes('color') || optionName.includes('colour')) {
              // Found the color option, get the corresponding value
              const optionKey = `option${index + 1}`;
              selectedColor = variant[optionKey];
            }
          }
        });

        if (!selectedColor) return;

        const normalizedColor = selectedColor.toLowerCase().trim();
        // Support both #color-black and #color_black formats
        const colorTagHyphen = `#color-${normalizedColor}`;
        const colorTagUnderscore = `#color_${normalizedColor}`;

        // Get all media items
        const allMediaItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));

        // Separate items into color-matched and non-matched
        const colorMatchedItems = [];
        const otherItems = [];

        allMediaItems.forEach((item) => {
          const altText = item.dataset.mediaAlt?.toLowerCase() || '';
          // Check for both formats: #color-black or #color_black
          if (altText.includes(colorTagHyphen) || altText.includes(colorTagUnderscore)) {
            colorMatchedItems.push(item);
          } else {
            otherItems.push(item);
          }
        });

        // If we have color-matched items, reorder the gallery
        if (colorMatchedItems.length > 0) {

          // Find the featured media item ID
          const featuredMediaFullId = `${this.dataset.section}-${variantFeaturedMediaId}`;

          // Reorder: color-matched items first, then others
          // But keep the featured media at the very first position
          const allItemsOrdered = [...colorMatchedItems, ...otherItems];

          // Find the featured media in our ordered list
          const featuredIndex = allItemsOrdered.findIndex(item => item.dataset.mediaId === featuredMediaFullId);

          if (featuredIndex > 0) {
            // Move featured media to the front
            const featuredItem = allItemsOrdered.splice(featuredIndex, 1)[0];
            allItemsOrdered.unshift(featuredItem);
          }

          // Now reorder the DOM elements
          allItemsOrdered.forEach((item, index) => {
            // Move each item to its correct position
            const currentPosition = Array.from(mediaGallerySource.children).indexOf(item);
            if (currentPosition !== index) {
              if (index === 0) {
                mediaGallerySource.prepend(item);
              } else {
                const referenceNode = mediaGallerySource.children[index];
                mediaGallerySource.insertBefore(item, referenceNode);
              }
            }
          });

          // Force load visible images that may have been lazy-loaded
          this.forceLoadVisibleImages(mediaGallerySource, isInitialLoad);

          // Re-apply show more/less functionality after reordering
          // Pass isInitialLoad to preserve state on initial page load
          this.reapplyShowMoreLess(isInitialLoad);

          // Also reorder thumbnails to match
          this.reorderThumbnails(allItemsOrdered);
        }
      }

      forceLoadVisibleImages(mediaGallerySource, isInitialLoad = false) {
        // Get ALL images in the gallery (including eager-loaded ones)
        const allMediaItems = mediaGallerySource.querySelectorAll('.product__media-item');
        
        allMediaItems.forEach((mediaItem, index) => {
          const img = mediaItem.querySelector('img');
          if (!img) return;

          // On initial load after color sorting, we need to load ALL images because:
          // 1. Images have been reordered, so we can't rely on original lazy loading positions
          // 2. User may click "Show More" and expect all images to display
          // 3. The first image might have changed after reordering
          // On variant change, also load ALL images so they're ready when user clicks "Show More"
          
          // Change loading attribute to eager to force immediate load
          if (img.loading === 'lazy') {
            img.loading = 'eager';
          }

          // If the image hasn't loaded yet, force it by setting src to itself
          if (!img.complete || img.naturalHeight === 0) {
            const currentSrc = img.src;
            if (currentSrc) {
              img.src = '';
              img.src = currentSrc;
            }
          }
        });
      }

      reorderThumbnails(orderedMediaItems) {
        const thumbnailGallery = this.querySelector('#GalleryThumbnails-' + this.dataset.section);
        if (!thumbnailGallery) return;

        const thumbnailList = thumbnailGallery.querySelector('ul');
        if (!thumbnailList) return;

        // Get all media IDs from the reordered main gallery in order
        const orderedMediaIds = orderedMediaItems.map(item => item.dataset.mediaId);

        // Get all thumbnail items
        const allThumbnails = Array.from(thumbnailList.querySelectorAll('li[data-target]'));

        // Create a map for quick lookup
        const thumbnailMap = new Map();
        allThumbnails.forEach(thumb => {
          thumbnailMap.set(thumb.dataset.target, thumb);
        });

        // Reorder thumbnails to match the main gallery order
        orderedMediaIds.forEach((mediaId, index) => {
          const thumbnail = thumbnailMap.get(mediaId);
          if (thumbnail) {
            const currentPosition = Array.from(thumbnailList.children).indexOf(thumbnail);
            if (currentPosition !== index) {
              if (index === 0) {
                thumbnailList.prepend(thumbnail);
              } else {
                const referenceNode = thumbnailList.children[index];
                thumbnailList.insertBefore(thumbnail, referenceNode);
              }
            }
          }
        });
      }

      reapplyShowMoreLess(isInitialLoad = false) {
        // Get the section ID from the current element
        const sectionId = this.dataset.section;
        const gallery = document.getElementById(`Slider-Gallery-${sectionId}`);
        const toggleButton = document.getElementById('toggleButton');

        if (!gallery || !toggleButton) return;

        // IMPORTANT: Always get a fresh NodeList after DOM reordering
        // This ensures we're working with the current order of slides
        const slides = gallery.querySelectorAll('.product__media-item');
        const visibleSlides = 6;
        const isMobile = window.innerWidth < 749;

        // Skip if mobile
        if (isMobile) return;

        // Check the current expanded state from the button
        const isExpanded = toggleButton.getAttribute('data-expanded') === 'true';

        // On initial load, set to collapsed state (first 6 visible)
        // On variant change, preserve the current state
        if (isInitialLoad) {
          // Initial load: first 6 visible, rest hidden
          slides.forEach((slide, index) => {
            if (index >= visibleSlides) {
              slide.style.display = 'none';
            } else {
              slide.style.display = 'block';
            }
          });
          // Make sure button is in correct initial state
          toggleButton.setAttribute('data-expanded', 'false');
          toggleButton.innerHTML = `Show More <span><svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-arrow" style="transform: rotate(90deg);width: 12px;" viewBox="0 0 14 10"><path fill="currentColor" fill-rule="evenodd" d="M8.537.808a.5.5 0 0 1 .817-.162l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 1 1-.708-.708L11.793 5.5H1a.5.5 0 0 1 0-1h10.793L8.646 1.354a.5.5 0 0 1-.109-.546" clip-rule="evenodd"/></svg></span>`;
        } else {
          // Variant change: preserve the current expanded/collapsed state
          slides.forEach((slide, index) => {
            if (index >= visibleSlides) {
              // Images beyond 6: show if expanded, hide if collapsed
              slide.style.display = isExpanded ? 'block' : 'none';
            } else {
              // First 6 images: always visible
              slide.style.display = 'block';
            }
          });
        }
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
